import { Store } from 'redux';
import isRecord from '@rsos/base-utils/isRecord';
import { createFetchRetry } from '@rsos/capstone';
import { getAPIHost } from '@rsos/utils/metaTags';

interface DiscrepancyReportingEnabled {
  enabled: true;
  targetState: string;
  clientID: string;
  clientSecret: string;
}

interface DiscrepancyReportingDisabled {
  enabled: false;
}

export type DiscrepancySettings =
  | DiscrepancyReportingEnabled
  | DiscrepancyReportingDisabled;

/**
 * Validates and extracts discrepancy settings from raw attributes response
 */
function validateDiscrepancySettings(
  rawData: unknown,
  orgName: string,
): DiscrepancySettings {
  if (!isRecord(rawData)) {
    return { enabled: false };
  }

  const isReportingEnabled = rawData.indigital_discrepancy_reporting_enabled;
  if (
    typeof isReportingEnabled !== 'string' ||
    isReportingEnabled.toLowerCase() !== 'true'
  ) {
    // eslint-disable-next-line no-console -- This seems harmless and useful for debugging
    console.debug(`Discrepancy reporting disabled for org ${orgName}`);
    return { enabled: false };
  }

  // eslint-disable-next-line no-console -- This seems harmless and useful for debugging
  console.debug(`Discrepancy reporting enabled for org ${orgName}`);

  // Enabled - validate required fields are strings
  const targetState = rawData.indigital_discrepancies_target_state;
  const clientId = rawData.indigital_discrepancies_client_id;
  const clientSecret = rawData.indigital_discrepancies_client_secret;

  if (
    typeof targetState !== 'string' ||
    typeof clientId !== 'string' ||
    typeof clientSecret !== 'string'
  ) {
    throw new Error(
      'When discrepancy reporting is enabled, target_state, client_id, and client_secret must be strings',
    );
  }

  return {
    enabled: true,
    targetState: targetState,
    clientID: clientId,
    clientSecret: clientSecret,
  };
}

/**
 * Fetches discrepancy settings for a given organization from the PSAP attributes endpoint.
 *
 * @param orgName - The organization name to fetch settings for
 * @returns Promise resolving to validated discrepancy settings
 * @throws Error if the request fails or orgName is not provided
 */
export async function fetchDiscrepancySettings(
  orgName: string,
  store: Store,
): Promise<DiscrepancySettings> {
  if (!orgName) {
    throw new Error(
      'Organization name is required to fetch discrepancy settings',
    );
  }

  const fetchRetry = createFetchRetry(store);

  const apiHost = getAPIHost();
  const url = `${apiHost}/v1/capstone/psaps/${orgName}/attributes`;

  const response = await fetchRetry(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch discrepancy settings: ${response.status} ${response.statusText}`,
    );
  }

  const data: unknown = await response.json();

  // Validate that response is an object
  if (!isRecord(data)) {
    throw new Error('Invalid response format from attributes endpoint');
  }

  return validateDiscrepancySettings(data, orgName);
}
